import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  addBugComment,
  createBugReport,
  deleteBugComment,
  deleteBugReport,
  getBugReport,
  getBugStats,
  getBugTimeline,
  listBugReports,
  updateBugReport,
  type BugAttachment,
  type BugFilters,
  type CreateBugReportInput,
  type UpdateBugReportInput,
} from '../lib/bugReportsApi';

// Refresh everything a single report appears in: its own detail, its timeline,
// the triage list, and the sidebar badge (a status change moves a report out of
// NEW, which is what the badge counts).
function invalidateBug(qc: ReturnType<typeof useQueryClient>, reportId?: string) {
  if (reportId) {
    void qc.invalidateQueries({ queryKey: ['bug-report', reportId] });
    void qc.invalidateQueries({ queryKey: ['bug-timeline', reportId] });
  }
  void qc.invalidateQueries({ queryKey: ['bug-reports'] });
  void qc.invalidateQueries({ queryKey: ['bug-report-stats'] });
}

export function useBugReports(filters: BugFilters) {
  return useQuery({
    queryKey: ['bug-reports', filters],
    queryFn: () => listBugReports(filters),
  });
}

export function useBugReport(id: string | undefined) {
  return useQuery({
    queryKey: ['bug-report', id],
    queryFn: () => getBugReport(id as string),
    enabled: Boolean(id),
  });
}

export function useBugTimeline(id: string | undefined) {
  return useQuery({
    queryKey: ['bug-timeline', id],
    queryFn: () => getBugTimeline(id as string),
    enabled: Boolean(id),
  });
}

/**
 * The sidebar badge. `enabled` because `/api/bug-reports/stats` is ADMIN-only:
 * asking as anyone else would just collect 403s on every page load.
 */
export function useBugReportStats(enabled: boolean) {
  return useQuery({
    queryKey: ['bug-report-stats'],
    queryFn: getBugStats,
    enabled,
    staleTime: 60_000,
  });
}

export function useCreateBugReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateBugReportInput) => createBugReport(input),
    onSuccess: () => invalidateBug(qc),
  });
}

export function useUpdateBugReport(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateBugReportInput) => updateBugReport(id, input),
    onSuccess: () => invalidateBug(qc, id),
  });
}

export function useDeleteBugReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteBugReport(id),
    onSuccess: (_data, id) => invalidateBug(qc, id),
  });
}

export function useAddBugComment(reportId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { content: string; attachments?: BugAttachment[] }) =>
      addBugComment(reportId, input.content, input.attachments),
    onSuccess: () => invalidateBug(qc, reportId),
  });
}

export function useDeleteBugComment(reportId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (commentId: string) => deleteBugComment(reportId, commentId),
    onSuccess: () => invalidateBug(qc, reportId),
  });
}
